import React, { useState } from "react";
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
} from "@fluentui/react";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import {
  OVERWRITE_DOCUMENT_CONFIRMATION_MSG,
  OVERWRITE_DOCUMENT_CONFIRMATION_TITLE,
  DELETE_DOCUMENT_TITLE,
  DELETE_DOCUMENT_SUB_TITLE,
} from "../../redux/constants/messageTypes";

const ConfirmModal = ({
  isOpen,
  onConfirm,
  onCancel,
  theme,

  type = "confirm",
  title = type === "delete"
    ? DELETE_DOCUMENT_TITLE
    : OVERWRITE_DOCUMENT_CONFIRMATION_TITLE,
  subText = type === "delete"
    ? DELETE_DOCUMENT_SUB_TITLE
    : OVERWRITE_DOCUMENT_CONFIRMATION_MSG,
  confirmButtonText = type === "delete" ? "Delete" : "Confirm",
  cancelButtonText = "Cancel",
  showCloseButton = false,
  disableDismiss = type === "delete",

  isDeleting = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!theme) {
    return null;
  }

  const handleConfirmClick = async () => {
    if (type === "delete" && (isDeleting || isLoading)) return;

    if (type === "delete") {
      setIsLoading(true);
      try {
        await onConfirm();
      } finally {
        setIsLoading(false);
      }
    } else {
      onConfirm();
    }
  };

  const isButtonDisabled = type === "delete" && (isDeleting || isLoading);
  const showProgress = type === "delete" && isButtonDisabled;

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={disableDismiss ? undefined : onCancel}
      dialogContentProps={{
        type: DialogType.normal,
        title: title,
        subText: subText,
        showCloseButton: showCloseButton && !disableDismiss,
        styles: {
          title: {
            fontFamily: theme.fonts.regular,
            fontWeight: 600,
            fontSize: `${theme.fontSizes?.medium ?? 16}px`,
            textAlign: "center",
            color: theme.colors.fontColor,
          },
          subText: {
            fontFamily: theme.fonts.regular,
            fontWeight: 400,
            fontSize: `${theme.fontSizes?.normal ?? 12}px`,
            textAlign: "center",
            color: theme.colors.fontColor,
            lineHeight: "1.4",
            // Allows callers (e.g. bulk overwrite confirmation) to put each item on its own line via "\n".
            whiteSpace: "pre-line",
          },
          content: {
            textAlign: "center",
          },
          closeButton: disableDismiss ? { display: "none" } : undefined,
        },
      }}
      modalProps={{
        isBlocking: true,
        styles: {
          main: {
            maxWidth: 450,
            fontFamily: theme.fonts.regular,
            backgroundColor: theme.colors.popupDialogBackgroundColor,
          },
          scrollableContent: {
            textAlign: "center",
          },
        },
      }}
    >
      <DialogFooter
        styles={{
          actions: {
            margin: "0 auto",
            textAlign: "center",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          },
          actionsRight: {
            margin: "0 auto",
            textAlign: "center",
            justifyContent: "center",
          },
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            fontFamily: theme.fonts.regular,
          }}
        >
          <DefaultButton
            onClick={onCancel}
            text={cancelButtonText}
            disabled={isButtonDisabled}
            styles={{
              root: {
                fontFamily: theme.fonts.regular,
                fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                color: theme.colors.themeDarkColor,
                fontWeight: 500,
                borderRadius: "20px",
                borderColor: theme.colors.themeDarkColor,
                backgroundColor: theme.colors.popupDialogBackgroundColor,
                opacity: isButtonDisabled ? 0.5 : 1,
              },
              rootHovered: {
                backgroundColor: `${theme.colors.themeDarkColor}10`,
                color: theme.colors.themeDarkColor,
                borderColor: theme.colors.themeDarkColor,
              },
              rootPressed: {
                backgroundColor: `${theme.colors.themeDarkColor}10`,
                color: theme.colors.themeDarkColor,
                borderColor: theme.colors.themeDarkColor,
              },
              rootDisabled: {
                color: theme.colors.themeDarkColor,
                borderColor: theme.colors.themeDarkColor,
                backgroundColor: theme.colors.popupDialogBackgroundColor,
              },
            }}
          />
          <PrimaryButton
            onClick={handleConfirmClick}
            disabled={isButtonDisabled}
            styles={{
              root: {
                fontFamily: theme.fonts.regular,
                fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                fontWeight: 500,
                borderColor: theme.colors.themeDarkColor,
                borderRadius: "20px",
                minWidth: "80px",
                opacity: isButtonDisabled ? 0.7 : 1,
                ...(type === "delete" &&
                  isButtonDisabled && {
                    backgroundColor: theme.colors.themeDarkColor,
                    borderColor: theme.colors.themeDarkColor,
                  }),
              },
              rootDisabled:
                type === "delete"
                  ? {
                      backgroundColor: theme.colors.themeDarkColor,
                      borderColor: theme.colors.themeDarkColor,
                    }
                  : undefined,
            }}
            onRenderText={
              type === "delete"
                ? (props, defaultRender) => {
                    if (showProgress) {
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          <Spinner
                            size={SpinnerSize.small}
                            styles={{
                              circle: {
                                borderColor: "white white transparent",
                                borderWidth: "1.5px",
                              },
                            }}
                          />
                          <span>Deleting...</span>
                        </div>
                      );
                    }
                    return defaultRender(props);
                  }
                : undefined
            }
          >
            {confirmButtonText}
          </PrimaryButton>
        </div>
      </DialogFooter>
    </Dialog>
  );
};

export default ConfirmModal;
